import {
  businessEntityKeys,
  createBusinessEntity,
  queryClient,
  updateBusinessEntity,
  useBusinessEntity,
  useSupportedCorporations,
} from '@commenda/api';
import { OnboardingStepNotificationEntity } from '@commenda/types/src/company/types';
import {
  CorporationModel,
  MonthIndexOfMonth,
  NonTimezoneSensitiveDate,
} from '@commenda/utils';
import { CorporationType, Country } from '@prisma/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';

export interface AddEntityFormData {
  incorporationCountry: Country | null;
  companyLegalName: string;
  dateOfIncorporation: string;
  /** The content's jurisdiction id, e.g. ADM1_US-DE. */
  jurisdiction: string | null;
  /** The content's legal form id, e.g. GLEIF:H0PO. What the customer actually picked. */
  entityLegalFormId: string | null;
  /**
   * The legacy name for the picked form, where the vocabulary has one. Derived from the form, not
   * chosen: most forms the content carries have no corporation type, and the backend takes none.
   */
  entityType: CorporationType | null;
  fiscalYearEnd: { month: string; day: string } | null;
  governanceStructureCode: string | null;
  isNonControlledEntity: boolean;
}

export type AddEntityFormErrors = Partial<
  Record<keyof AddEntityFormData | 'fiscalYearEnd.month' | 'fiscalYearEnd.day', string>
>;

const EMPTY_FORM: AddEntityFormData = {
  incorporationCountry: null,
  companyLegalName: '',
  dateOfIncorporation: '',
  jurisdiction: null,
  entityLegalFormId: null,
  entityType: null,
  fiscalYearEnd: null,
  governanceStructureCode: null,
  isNonControlledEntity: false,
};

interface UseAddEntityFormProps {
  onClose: () => void;
  companyId: number;
  businessEntityId?: number;
  shouldNotResetForm?: boolean;
  onSuccess?: (corporation: OnboardingStepNotificationEntity) => Promise<void>;
  defaultValues?: Partial<AddEntityFormData>;
  allowEmptyEntityName?: boolean;
  mode?: 'admin' | 'user';
  // When true, the `isNonControlledEntity` flag is included in the create
  // payload. The bottomsheet shell sets this; the modal does not.
  includeNonControlledEntity?: boolean;
  // Validation must mirror what's rendered. The bottomsheet hides these
  // fields; without these flags, validate() would block submit on phantom
  // errors against fields the user can't see.
  skipDateOfIncorporationValidation?: boolean;
  skipFiscalYearEndValidation?: boolean;
}

export function useAddEntityForm({
  onClose,
  companyId,
  businessEntityId,
  shouldNotResetForm = false,
  onSuccess,
  defaultValues,
  allowEmptyEntityName = false,
  mode = 'user',
  includeNonControlledEntity = false,
  skipDateOfIncorporationValidation = false,
  skipFiscalYearEndValidation = false,
}: UseAddEntityFormProps) {
  const { setNotification } = useNotification();

  const { data: supportedCorporations, error: loadError } =
    useSupportedCorporations();

  const initialFormData = useMemo<AddEntityFormData>(
    () => ({
      ...EMPTY_FORM,
      ...defaultValues,
    }),
    // Only compute once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [formData, setFormData] = useState<AddEntityFormData>(initialFormData);
  const [errors, setErrors] = useState<AddEntityFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialFormDataRef = useRef<AddEntityFormData>(initialFormData);

  const isRequired = mode !== 'admin';

  const updateField = useCallback(
    <K extends keyof AddEntityFormData>(key: K, value: AddEntityFormData[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  const selectedCountry = formData.incorporationCountry;

  const { data: businessEntity, error: businessEntityError } =
    useBusinessEntity(businessEntityId);

  useEffect(() => {
    if (!businessEntity) return;

    const newData: AddEntityFormData = {
      incorporationCountry:
        businessEntity.generalCorporationInfo.incorporationCountry,
      companyLegalName: businessEntity.generalCorporationInfo.legalName ?? '',
      dateOfIncorporation:
        businessEntity.generalCorporationInfo.formationDate ?? '',
      jurisdiction: businessEntity.generalCorporationInfo.jurisdiction,
      entityLegalFormId:
        businessEntity.generalCorporationInfo.entityLegalFormId ?? null,
      entityType: businessEntity.generalCorporationInfo.businessEntityType,
      governanceStructureCode:
        businessEntity.generalCorporationInfo.governanceStructureCode ?? null,
      fiscalYearEnd: businessEntity.accountingInfo?.fiscalYear?.data?.FIXED_DATE
        ? {
            month:
              MonthIndexOfMonth[
                businessEntity.accountingInfo.fiscalYear.data.FIXED_DATE.month
              ]?.toString() ?? '',
            day:
              businessEntity.accountingInfo.fiscalYear.data.FIXED_DATE.day?.toString() ??
              '',
          }
        : null,
      isNonControlledEntity: false,
    };

    setFormData(newData);
    initialFormDataRef.current = newData;
  }, [businessEntity]);

  useEffect(() => {
    if (!selectedCountry || !supportedCorporations) return;

    // Whether we know anything about a country is the content's answer, not a list here. Where it
    // offers nothing there is nothing to pick: no jurisdiction, and an entity type we do not know.
    // Null is what an unestablished jurisdiction is now — the enum's sentinel is gone.
    if (
      Object.keys(supportedCorporations?.jurisdictions?.[selectedCountry] || {})
        .length === 0
    ) {
      setFormData((prev) => ({
        ...prev,
        jurisdiction: null,
        entityLegalFormId: null,
        entityType: CorporationType.Unknown,
        fiscalYearEnd: null,
      }));
      return;
    }

    const jurisdictions = Object.keys(
      supportedCorporations?.jurisdictions?.[selectedCountry] || {},
    );

    let newJurisdiction: string | null;
    if (jurisdictions.length === 1) {
      newJurisdiction = jurisdictions[0]!;
    } else {
      const currentJurisdiction =
        businessEntity?.generalCorporationInfo?.jurisdiction;
      if (currentJurisdiction && jurisdictions.includes(currentJurisdiction)) {
        newJurisdiction = currentJurisdiction;
      } else {
        newJurisdiction = null;
      }
    }

    const effectiveJurisdiction = newJurisdiction || jurisdictions[0]!;
    const formsById =
      supportedCorporations?.entityTypes?.[selectedCountry]?.[
        effectiveJurisdiction
      ] || {};
    // Sorted so the corporation-type fallback below picks the same form every time.
    const offeredForms = Object.entries(formsById)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([, form]) => form);

    // The stamp answers first; several forms can share a corporation type, so deriving over a
    // stamp can land on a form the backend will not follow.
    const storedFormId =
      businessEntity?.generalCorporationInfo?.entityLegalFormId;
    const currentEntityType =
      businessEntity?.generalCorporationInfo?.businessEntityType;
    const selectedForm =
      (storedFormId
        ? offeredForms.find((form) => form.entityLegalFormId === storedFormId)
        : undefined) ??
      (offeredForms.length === 1
        ? offeredForms[0]
        : (currentEntityType
            ? offeredForms.find(
                (form) => form.corporationType === currentEntityType,
              )
            : undefined));

    const resolved = {
      jurisdiction: newJurisdiction,
      entityLegalFormId: selectedForm?.entityLegalFormId ?? null,
      entityType: selectedForm?.corporationType ?? null,
    };

    setFormData((prev) => ({ ...prev, ...resolved }));

    // The baseline moves with the resolution, or an untouched edit form opens already dirty.
    if (businessEntity) {
      initialFormDataRef.current = {
        ...initialFormDataRef.current,
        ...resolved,
      };
    }

    // An entity's own year end, where it has one. There is nothing country-specific about
    // showing a value the entity already holds.
    const storedFiscalYearEnd =
      businessEntity?.accountingInfo?.fiscalYear?.data?.FIXED_DATE;

    if (storedFiscalYearEnd?.month || storedFiscalYearEnd?.day) {
      setFormData((prev) => ({
        ...prev,
        fiscalYearEnd: {
          month:
            MonthIndexOfMonth[storedFiscalYearEnd.month]?.toString() ?? '',
          day: storedFiscalYearEnd.day?.toString() ?? '',
        },
      }));
    }
  }, [
    selectedCountry,
    businessEntityId,
    businessEntity,
    supportedCorporations,
  ]);

  // Content-driven: show the jurisdiction picker when the deployed business-entity
  // content has more than one jurisdiction for this country (US states, CA provinces,
  // AE emirates, CH cantons, etc.). Single-jurisdiction countries (PH, IN, GB, …)
  // auto-pick JUR_*__GENERAL silently.
  // What the country offers, from the content. A jurisdiction only needs asking about when there
  // is more than one to pick from.
  const offeredByJurisdiction = selectedCountry
    ? (supportedCorporations?.entityTypes?.[selectedCountry as Country] ?? {})
    : {};

  const shouldShowJurisdictionInput =
    selectedCountry != null &&
    Object.keys(
      supportedCorporations?.jurisdictions?.[selectedCountry as Country] || {},
    ).length > 1;

  // Before a jurisdiction is chosen, anything the country offers anywhere counts: with a single
  // jurisdiction the input above is hidden and one is never chosen explicitly.
  const offeredEntityTypes = formData.jurisdiction
    ? (offeredByJurisdiction[formData.jurisdiction] ?? {})
    : Object.assign({}, ...Object.values(offeredByJurisdiction));

  const shouldShowEntityTypeInput = Object.keys(offeredEntityTypes).length > 0;

  // The content says whether an entity picks its own year end. Where it does not, the year end
  // follows from the legal form and there is nothing to ask.
  const selectedEntityType = formData.entityLegalFormId
    ? offeredEntityTypes[formData.entityLegalFormId]
    : undefined;

  // What the law allows for the form the customer picked. The backend refuses a year end a form
  // has no lawful path to, so the form should not offer one in the first place.
  const fiscalYearRule = selectedEntityType?.fiscalYearRule ?? null;

  // Asked where the entity may choose, and where the content says nothing — every entity has a
  // year end whether or not its form is modelled yet, and the backend takes any value when it has
  // no rule to check against. Hidden only where a rule exists, forbids choosing, and names no date:
  // the one case with genuinely nothing to show and nothing to ask.
  const shouldShowFiscalYearInput = selectedEntityType
    ? fiscalYearRule === null ||
      fiscalYearRule.isModifiable ||
      fiscalYearRule.default !== undefined
    : shouldShowEntityTypeInput;

  // A fixed end the entity has no lawful path away from. Shown, but not up for editing.
  const isFiscalYearLocked =
    fiscalYearRule !== null &&
    !fiscalYearRule.isModifiable &&
    fiscalYearRule.default !== undefined;

  // Open on the year end the content says this legal form uses, rather than an empty pair of
  // dropdowns. Only fills a blank: an entity's own value and anything the user has picked stand.
  const contentFiscalYearEnd = fiscalYearRule?.default;

  useEffect(() => {
    if (!contentFiscalYearEnd) return;

    const month = MonthIndexOfMonth[contentFiscalYearEnd.month];
    const { day } = contentFiscalYearEnd;
    if (!month || !day) return;

    setFormData((prev) =>
      prev.fiscalYearEnd?.month || prev.fiscalYearEnd?.day
        ? prev
        : {
            ...prev,
            fiscalYearEnd: { month: month.toString(), day: day.toString() },
          },
    );
  }, [
    contentFiscalYearEnd?.month,
    contentFiscalYearEnd?.day,
  ]);

  const isDirty =
    JSON.stringify(formData) !== JSON.stringify(initialFormDataRef.current);

  const isLoading =
    !supportedCorporations || (businessEntityId && !businessEntity);

  // What the content permits for the type the customer picked. A form that names no default makes
  // the entity choose, so the choice has to be asked for rather than assumed.
  const governanceStructure =
    (formData.jurisdiction && formData.entityLegalFormId
      ? supportedCorporations?.entityTypes?.[selectedCountry as Country]?.[formData.jurisdiction]?.[formData.entityLegalFormId]?.governanceStructure
      : null) ?? null;

  const governanceOptions = governanceStructure?.permitted ?? [];
  const requiresGovernanceElection =
    governanceStructure !== null && governanceStructure.default === undefined && governanceOptions.length > 0;
  // A default is what the form falls back to, not the only arrangement it allows.
  const offersGovernanceElection = requiresGovernanceElection || governanceOptions.length > 1;
  // Changing the entity type leaves the form it was picked for behind. An election that the new
  // form does not permit is not an election, and posting it fails the backend's check.
  const electedStructureCode = governanceOptions.some(
    (structure) => structure.structureCode === formData.governanceStructureCode,
  )
    ? formData.governanceStructureCode
    : null;

  const validate = (): AddEntityFormErrors => {
    const errs: AddEntityFormErrors = {};

    // Admin mode is fully permissive.
    if (!isRequired) return errs;

    if (!formData.incorporationCountry) {
      errs.incorporationCountry = 'Select a country.';
    }

    if (
      !allowEmptyEntityName &&
      formData.incorporationCountry &&
      !formData.companyLegalName.trim()
    ) {
      errs.companyLegalName = 'Company legal name is required.';
    }

    if (formData.companyLegalName.length > 255) {
      errs.companyLegalName =
        'Company legal name must be less than 255 characters.';
    }

    if (
      !skipDateOfIncorporationValidation &&
      formData.incorporationCountry &&
      !formData.dateOfIncorporation
    ) {
      errs.dateOfIncorporation = 'Date of incorporation is required.';
    }

    if (shouldShowJurisdictionInput && !formData.jurisdiction) {
      errs.jurisdiction = 'Select a jurisdiction.';
    }

    if (requiresGovernanceElection && !electedStructureCode) {
      errs.governanceStructureCode = 'Select how this entity is governed.';
    }

    if (shouldShowEntityTypeInput && !formData.entityLegalFormId) {
      errs.entityType = 'Select an entity type.';
    }

    if (!skipFiscalYearEndValidation && shouldShowFiscalYearInput) {
      if (!formData.fiscalYearEnd?.month) {
        errs['fiscalYearEnd.month'] = 'Select a month.';
      }
      if (!formData.fiscalYearEnd?.day) {
        errs['fiscalYearEnd.day'] = 'Select a day.';
      }
    }

    return errs;
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setErrors({});
    initialFormDataRef.current = { ...EMPTY_FORM };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Bottomsheet variant nests inside the shareholder `<form>` (Radix
    // portals don't break React event bubbling). Without stopPropagation
    // the outer form also submits.
    e.stopPropagation();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const { fiscalYearEnd } = formData;
      const hasFiscalYearEnd = Boolean(
        fiscalYearEnd?.day && fiscalYearEnd?.month,
      );
      // A year end the form collected gets sent. Which countries that applies to is settled by
      // whether the form asked at all, which the content decides.
      const fiscalYearStart = hasFiscalYearEnd
        ? NonTimezoneSensitiveDate.convertFiscalYearEndToStart(
            +fiscalYearEnd!.day,
            +fiscalYearEnd!.month,
          )
        : null;

      // Admin mode sends only populated fields so a partial edit doesn't
      // wipe existing values. A create always sends the whole entity.
      const trimmedLegalName = formData.companyLegalName.trim();
      // The column is not nullable and the picker can now land on a form the legacy vocabulary
      // never named. Unknown is what that column already carries for exactly that case; the legal
      // form sent alongside it is what identifies the entity.
      const corporationType = formData.entityLegalFormId
        ? (formData.entityType ?? CorporationType.Unknown)
        : formData.entityType;
      const legalForm = formData.entityLegalFormId
        ? { entityLegalFormId: formData.entityLegalFormId }
        : {};
      const wholeEntity = {
        incorporationCountry: formData.incorporationCountry!,
        legalName: trimmedLegalName,
        jurisdiction: formData.jurisdiction,
        corporationType: corporationType ?? CorporationType.Unknown,
        ...legalForm,
      };
      const populatedEntity = {
        ...(formData.incorporationCountry
          ? { incorporationCountry: formData.incorporationCountry }
          : {}),
        ...(trimmedLegalName ? { legalName: trimmedLegalName } : {}),
        ...(formData.jurisdiction
          ? { jurisdiction: formData.jurisdiction }
          : {}),
        ...(corporationType ? { corporationType } : {}),
        ...legalForm,
      };
      const payload = {
        ...(formData.dateOfIncorporation
          ? { formationDate: formData.dateOfIncorporation }
          : {}),
        ...(fiscalYearStart
          ? {
              fiscalYearDay: fiscalYearStart.fiscalYearStartDate,
              fiscalYearMonth: fiscalYearStart.fiscalYearStartMonth,
            }
          : {}),
        ...(offersGovernanceElection && electedStructureCode
          ? { governanceStructureCode: electedStructureCode }
          : {}),
      };

      const res = businessEntity
        ? await updateBusinessEntity(businessEntity.id, {
            ...payload,
            ...(isRequired ? wholeEntity : populatedEntity),
          })
        : await createBusinessEntity(companyId, {
            ...payload,
            ...wholeEntity,
            // An edit cannot change it, so only a create sends it.
            ...(includeNonControlledEntity
              ? { isNonControlledEntity: formData.isNonControlledEntity }
              : {}),
          });

      if (!res.ok) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message:
            'Something went wrong while adding the entity, please try again or contact Team Commenda if you need help.',
        });
        return;
      }

      // An update answers with no body and a create with the new id alone.
      const id = businessEntity
        ? businessEntity.id
        : ((await res.json()) as { businessEntity: { id: number } }).businessEntity.id;

      await queryClient.invalidateQueries({
        queryKey: [
          businessEntityKeys.FETCH_BUSINESS_ENTITIES_OF_COMPANY,
          +companyId,
        ],
      });

      await queryClient.invalidateQueries({
        queryKey: [
          businessEntityKeys.FETCH_BUSINESS_ENTITY,
          +(businessEntityId ?? 0),
        ],
      });

      // Org chart reads from the same BusinessEntity set.
      await queryClient.invalidateQueries({
        queryKey: [
          businessEntityKeys.FETCH_COMPANY_ORG_STRUCTURE,
          +companyId,
        ],
      });

      if (onSuccess)
        await onSuccess({
          id,
          name: trimmedLegalName || businessEntity?.generalCorporationInfo.legalName,
          incorporationCountry:
            formData.incorporationCountry ?? businessEntity?.generalCorporationInfo.incorporationCountry,
          corporationType:
            corporationType ?? businessEntity?.generalCorporationInfo.businessEntityType,
        });

      setNotification({
        show: true,
        valid: true,
        title: businessEntityId ? 'Entity details edited!' : 'Entity added!',
        message: businessEntityId
          ? 'These details can be changed at any time.'
          : 'The entity has been added successfully.',
      });

      if (!shouldNotResetForm) resetForm();
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const requestClose = () => {
    if (!shouldNotResetForm) resetForm();
    onClose();
  };

  const jurisdictionOptions = CorporationModel.getJurisdictionList(
    selectedCountry,
    supportedCorporations,
  );

  const entityTypeOptions = CorporationModel.getEntityTypeOptions(
    selectedCountry,
    formData.jurisdiction,
    supportedCorporations,
  );

  return {
    formData,
    errors,
    isSubmitting,
    isDirty,
    setFormData,
    setErrors,
    updateField,
    handleSubmit,
    requestClose,
    isRequired,
    selectedCountry,
    shouldShowJurisdictionInput,
    shouldShowEntityTypeInput,
    shouldShowFiscalYearInput,
    isFiscalYearLocked,
    governanceOptions,
    requiresGovernanceElection,
    offersGovernanceElection,
    electedStructureCode,
    jurisdictionOptions,
    entityTypeOptions,
    isLoading,
    loadError: loadError ?? businessEntityError,
    businessEntityId,
  };
}

export type AddEntityFormController = ReturnType<typeof useAddEntityForm>;
